import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, ElementRef, OnDestroy, OnInit, Signal, signal, ViewChild, WritableSignal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { BehaviorSubject, combineLatest, Observable, of, Subscription } from 'rxjs';
import { catchError, finalize, first, map, shareReplay, switchMap } from 'rxjs/operators';
import {
  DsButtonVariants,
  DsChipColorVariant,
  DsChipSize,
  DsModalRef,
  DsModalService,
  DsModalVariant,
  DsToggleLabelPosition,
  DsTreeMenuItem,
  HeadingLevelType,
  Icons,
  DsBannerState,
} from '@levelaccess/design-system';
import { ActivatedRoute, Params } from '@angular/router';
import { $academy } from '@shared/constants/academy';
import { $artificialIntelligence, $privacySettings, $tenant, $tenantMetadata } from '@shared/constants/tenant';
import { $user } from '@shared/constants/user';
import { IUserServerResponse } from '@shared/interfaces/user.interface';
import { FeatureFlagCollection } from '@shared/interfaces/feature-flag.interface';
import { Api, ApiQueryOption } from '@shared/constants/api';
import { $sortingOrder } from '@shared/constants/sort';
import {
  $digitalProperty,
  $digitalPropertyType,
  DigitalPropertyStatus,
  DigitalPropertyTypeNames,
} from '@shared/constants/digital-properties';
import { RequiredSecurities } from '@shared/constants/required-securities';
import { $workspace, workspaceStatus } from '@shared/constants/workspace';
import {
  IDigitalPropertiesListResponse,
  IDigitalProperty,
  IDigitalPropertyListItem,
  IDigitalPropertyType,
} from '@shared/interfaces/digital-property.interface';
import {
  ITenantDigitalPropertyLimitState,
  ITenantDigitalPropertyUserWaySiteState,
  ITenantInfoWithPackageInfo,
  ITenantWithPackageId,
} from '@shared/interfaces/tenant.interface';
import { $package } from '@shared/constants/packaging';
import { IPackage } from '@shared/interfaces/packaging.interface';
import { IGetWorkspacesResponse, IRestoreWorkspaceResponse, IWorkspace } from '@shared/interfaces/workspace.interface';
import { SharedCommonUtility } from '@shared/utils/common.utility';
import { AiService } from '../../../services/ai.service';
import { AppConfigService } from '../../../services/app-config.service';
import { DigitalPropertyService } from '../../../services/digital-property.service';
import { ExternalLinkService } from '../../../services/external-link.service';
import { UserWayNavigationService } from '../../../services/userway-navigation.service';
import { FeatureFlagService } from '../../../services/feature-flag/feature-flag.service';
import { LoadErrorHandler } from '../../../services/load-error-handler';
import { ErrorMessageService } from '../../../services/error-message.service';
import { NavService } from '../../../services/navigation/nav.service';
import { RoutingService } from '../../../services/routing.service';
import { StandardizedHeaderBarComponent } from '../../../services/standardized-header-bar-aware.component';
import { TenantService } from '../../../services/tenant.service';
import { UserPropertyService } from '../../../services/user-property.service';
import { UserService } from '../../../services/user.service';
import { WorkspaceService } from '../../../services/workspace.service';
import { PackageService } from '../../../services/package.service';
import { CommonUtility } from '../../../utility/common.utility';
import { AddDigitalPropertiesModalComponent } from './add-digital-properties-modal/add-digital-properties-modal.component';
import { AddWorkspaceModalComponent } from './add-workspace-modal/add-workspace-modal.component';
import {
  DigitalPropertyStateAction,
  SelfmanageChangeDigitalPropertyStateModalComponent,
} from './selfmanage-change-asset-state-modal/selfmanage-change-digital-property-state-modal.component';
import { MoveDigitalPropertyModalComponent } from './move-digital-property-modal/move-digital-property-modal.component';
import {
  SelfmanageChangeWorkspaceStateModalComponent,
  WorkspaceStateAction,
} from './selfmanage-change-workspace-state-modal/selfmanage-change-workspace-state-modal.component';
import { EditDigitalPropertyModalComponent } from './edit-digital-property-modal/edit-digital-property-modal.component';
import { EditWorkspaceModalComponent } from './edit-workspace-modal/edit-workspace-modal.component';
import { CannotMoveDigitalPropertyModalComponent } from './cannot-move-digital-property-modal/cannot-move-digital-property-modal.component';
import { BusMessageChannels, BusMessageService } from '../../../services/bus-message.service';
import { TranslateService } from '../../../translate/translate.service';
import { NotificationService } from '../../../services/notification.service';
import { NotificationPosition } from '../../../models/notification.model';
import { AclSecurityAdapter } from '../../../services/acl.service';
import { UserAclService } from '../../../services/user-acl.service';
import { SharedModule } from '../../../shared/shared.module';
import { SharedTenantsModule } from '../../../admin/tenants/shared-tenants.module';
import { PortfolioSharedModule } from '../portfolio/portfolio-shared.module';
import { OrganizationUsersComponent } from './organization-users/organization-users.component';
import { WorkspaceUsersComponent } from './workspace-users/workspace-users.component';
import { DigitalPropertyUsersComponent } from './digital-property-users/digital-property-users.component';
import { UserWaySsoErrorBannerComponent } from '../../../components/userway-sso-error-banner/userway-sso-error-banner.component';

interface IFeatureUsage {
  count: number;
  limit: number;
}

interface IDisplayFieldConfig {
  label: string;
  field: string;
}

enum SelectedTreeItemType {
  organization = 'organization',
  workspace = 'workspace',
  website = 'website',
  anotherAsset = 'anotherAsset',
}

@Component({
  selector: 'app-organization-details',
  styleUrl: './organization-details.component.scss',
  templateUrl: './organization-details.component.html',
  imports: [
    SharedModule,
    SharedTenantsModule,
    PortfolioSharedModule,
    UserWaySsoErrorBannerComponent,
    OrganizationUsersComponent,
    WorkspaceUsersComponent,
    DigitalPropertyUsersComponent,
  ],
  standalone: true,
})
export class OrganizationDetailsComponent extends StandardizedHeaderBarComponent implements OnInit, OnDestroy {
  public readonly isAdminView: boolean = this.appConfigService.isTenantEnabled() === false;
  protected readonly $digitalProperty: typeof $digitalProperty = $digitalProperty;

  protected pageTitle: string;
  protected activeTabId: number = 0;
  public nodeDetailTabId: number = 0;
  protected subscriptions: Subscription;
  protected selectedTreeItemId: WritableSignal<string | null> = signal(null);

  protected readonly tenantIdFromRoute: Signal<string | undefined> = toSignal(
    this.activatedRoute.params.pipe(map((params: Params) => params[Api.tenantId])),
  );

  public readonly currentTenantUnmapped$: Observable<ITenantWithPackageId | ITenantInfoWithPackageInfo> = this.isAdminView
    ? this.tenantService.getTenant(this.tenantIdFromRoute())
    : this.userService.currentTenant$;

  private mapTenantWithPackage(tenant: ITenantWithPackageId, packages: IPackage[]): ITenantInfoWithPackageInfo {
    const packageId: string | undefined = tenant[$tenantMetadata.packageId];
    const pkg: IPackage | undefined = packages.find((p: IPackage) => p[$package._id] === packageId);

    return {
      ...tenant,
      [$tenantMetadata.packageId]: packageId ?? '',
      packageName: pkg?.[$package.name] ?? '',
      packageAddons: [],
    };
  }

  private buildAdminTenantStream(): Observable<ITenantInfoWithPackageInfo> {
    return combineLatest([this.currentTenantUnmapped$, this.packageService.getPackages()]).pipe(
      map(([tenant, packages]: [ITenantWithPackageId, IPackage[]]) => this.mapTenantWithPackage(tenant, packages)),
    );
  }

  private buildTenantStream(): Observable<ITenantInfoWithPackageInfo> {
    return this.isAdminView ? this.buildAdminTenantStream() : this.userService.currentTenant$;
  }

  public readonly currentTenant: Signal<ITenantInfoWithPackageInfo | null> = toSignal<ITenantInfoWithPackageInfo | null>(
    this.buildTenantStream().pipe(
      catchError((error: HttpErrorResponse) => {
        this.loadErrorHandler.handleError('get_tenant_error', error, OrganizationDetailsComponent.name);
        return of(null);
      }),
    ),
  );

  protected readonly tenantId: Signal<string> = computed<string>(() =>
    this.isAdminView ? this.tenantIdFromRoute() : this.currentTenant()?.[$tenant._id],
  );

  public readonly isAcademyEnabled: Signal<boolean> = this.isAdminView
    ? toSignal(
        this.currentTenantUnmapped$.pipe(
          map((tenant: ITenantWithPackageId) => {
            return (
              SharedCommonUtility.notNullishOrEmpty(tenant[$tenant.academyConfig]?.[$academy.academyId]) ||
              SharedCommonUtility.notNullishOrEmpty(tenant[$tenant.academyConfig]?.[$academy.externalPackageSignupField])
            );
          }),
        ),
      )
    : signal(false);

  public showUserWaySsoErrorBanner: boolean = false;

  @ViewChild('descriptionCard', { read: ElementRef })
  private descriptionCardRef: ElementRef<HTMLElement>;

  public selectedItemType: WritableSignal<SelectedTreeItemType> = signal<SelectedTreeItemType>(SelectedTreeItemType.organization);
  public selectedWorkspace: WritableSignal<IWorkspace | null> = signal<IWorkspace | null>(null);
  public selectedDigitalProperty: WritableSignal<IDigitalPropertyListItem | null> = signal<IDigitalPropertyListItem | null>(null);
  public userWayWidgetEnabled: Signal<boolean> = computed(() =>
    SharedCommonUtility.notNullish(this.selectedDigitalProperty()?.[$digitalProperty.userWaySiteHostName]),
  );

  public descriptionCardTitle: WritableSignal<string> = signal<string>('');

  private pendingSelectedItemId: string | null = null;

  public organizationTree: WritableSignal<DsTreeMenuItem[]> = signal([]);
  private readonly allWorkspaces: WritableSignal<IWorkspace[]> = signal([]);
  private readonly allDigitalProperties: WritableSignal<IDigitalPropertyListItem[]> = signal([]);
  private organizationTreeSubscription: Subscription | null = null;

  protected readonly availableDestinationWorkspaces: Signal<IWorkspace[]> = computed(() => {
    const currentWorkspaceId: string =
      this.selectedDigitalProperty()?.[$digitalProperty.workspace]?.[$workspace._id]?.toString() ??
      this.selectedWorkspace()?.[$workspace._id]?.toString();
    return this.allWorkspaces().filter(
      (w: IWorkspace) =>
        SharedCommonUtility.notNullish(currentWorkspaceId) &&
        w[$workspace.status] === workspaceStatus.active &&
        w[$workspace._id].toString() !== currentWorkspaceId,
    );
  });
  protected readonly isMoveValidationInProgress: WritableSignal<boolean> = signal<boolean>(false);

  protected readonly tenantUrl: Signal<string> = computed<string>(
    () => `https://${this.currentTenant()?.[$tenant.domain]}.${this.appConfigService.getTenantBaseUrl()}`,
  );
  protected readonly widgetsUsage: Signal<IFeatureUsage> = toSignal(
    this.tenantService.getDigitalPropertyUserWaySiteLimitState(this.tenantId()).pipe(
      map<ITenantDigitalPropertyUserWaySiteState, IFeatureUsage>((state: ITenantDigitalPropertyUserWaySiteState) => ({
        count: state?.userWaySiteCount ?? 0,
        limit: state?.limit ?? 0,
      })),
      catchError((error: HttpErrorResponse) => {
        this.loadErrorHandler.handleError('get_userway_site_limit_error', error, OrganizationDetailsComponent.name);
        return of<IFeatureUsage>({ count: 0, limit: 0 });
      }),
    ),
    {
      initialValue: { count: 0, limit: 0 },
    },
  );
  protected readonly digitalAssetsUsage: Signal<IFeatureUsage> = toSignal(
    this.currentTenantUnmapped$.pipe(
      switchMap((tenant: ITenantWithPackageId) => {
        if (SharedCommonUtility.notNullish(tenant[$tenant._id])) {
          return this.tenantService.getDigitalPropertyLimitState(tenant[$tenant._id]).pipe<IFeatureUsage>(
            map<ITenantDigitalPropertyLimitState, IFeatureUsage>((state: ITenantDigitalPropertyLimitState) => ({
              count: state?.digitalPropertyCount ?? 0,
              limit: state?.digitalPropertyLimit ?? 0,
            })),
          );
        }
        return of<IFeatureUsage>({ count: 0, limit: 0 });
      }),
      catchError((error: HttpErrorResponse) => {
        this.loadErrorHandler.handleError('get_digital_property_limit_error', error, OrganizationDetailsComponent.name);
        return of<IFeatureUsage>({ count: 0, limit: 0 });
      }),
    ),
    {
      initialValue: { count: 0, limit: 0 },
    },
  );
  protected readonly isScanningAgentEnabled: Signal<boolean> = toSignal<boolean>(
    this.featureFlagService.variation$(FeatureFlagCollection.scanningAgent, false),
  );

  protected readonly isAiEnabled: Signal<boolean> = this.isAdminView
    ? computed(() => {
        const enabled = this.currentTenant()?.[$tenant.artificialIntelligence]?.[$artificialIntelligence.enabled];
        return this.isAiFeatureFlagEnabled() && this.appConfigService.getAiEnabled() && enabled !== false;
      })
    : toSignal(this.aiService.aiEnabledForTenant$);

  protected readonly isAiFeatureFlagEnabled: Signal<boolean> = toSignal<boolean>(
    this.featureFlagService.variation$(FeatureFlagCollection.artificialIntelligenceOptin, false).pipe(map(Boolean)),
  );

  protected readonly aiFeatureTextKey: Signal<string> = computed<string>(() =>
    this.isAiEnabled() ? 'organization_details_ai_features_text_opt_in' : 'organization_details_ai_features_text_opt_out',
  );

  protected readonly aiChipTextKey: Signal<string> = computed<string>(() =>
    this.isAiEnabled() ? 'organization_details_ai_chip_opt_in' : 'organization_details_ai_chip_opt_out',
  );
  protected readonly isDigitalAssetsPrivacySettingEnabled: Signal<boolean> = toSignal<boolean>(
    this.featureFlagService.variation$(FeatureFlagCollection.digitalAssetsPrivacySetting, false).pipe(map(Boolean)),
  );

  protected readonly isConfidentialScanningEnabled: Signal<boolean> = computed<boolean>(
    () => this.currentTenant()?.[$tenant.privacySettings]?.[$privacySettings.privacyObfuscationEnabled] === true,
  );

  protected readonly confidentialFeatureTextKey: Signal<string> = computed<string>(() =>
    this.isConfidentialScanningEnabled()
      ? 'organization_details_confidential_scanning_text_enabled'
      : 'organization_details_confidential_scanning_text_disabled',
  );

  protected readonly isSelfManageOrgHierarchyEnabled: Signal<boolean> = toSignal<boolean>(
    this.featureFlagService.variation$(FeatureFlagCollection.selfManageOrgHierarchy, false).pipe(map(Boolean)),
  );
  protected readonly isAtlasUserManagementEnabled: Signal<boolean> = toSignal<boolean>(
    this.featureFlagService.variation$(FeatureFlagCollection.atlasUserManagement, false).pipe(map(Boolean)),
  );
  protected readonly showInternalNotesHideDescription: Signal<boolean> = toSignal<boolean>(
    this.featureFlagService.variation$(FeatureFlagCollection.showInternalNotesHideDescription, false).pipe(map(Boolean)),
  );

  private readonly showArchivedItems$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  protected readonly showArchivedItems: Signal<boolean> = toSignal<boolean, false>(this.showArchivedItems$, {
    initialValue: false,
  });

  private readonly hasWorkspaceArchivePermission: Signal<boolean> = toSignal<boolean, false>(
    this.userAclService
      .createCheckAccessForCurrentUser()
      .pipe(
        map((adapter: AclSecurityAdapter): boolean =>
          adapter.useEntityLevel().useFunctionalActions(RequiredSecurities.Workspaces_Update.functionalActions).check(),
        ),
      ),
    { initialValue: false },
  );

  public readonly hasAccessToAcademy: Signal<boolean> = this.isAdminView
    ? toSignal(
        this.userAclService
          .createCheckAccessForCurrentUser()
          .pipe(
            map((adapter: AclSecurityAdapter): boolean =>
              adapter.useFunctionalActions(RequiredSecurities.Academy_Read.functionalActions).check(),
            ),
          ),
      )
    : signal(false);

  protected readonly canArchiveWorkspace: Signal<boolean> = computed(
    () => this.hasWorkspaceArchivePermission() && this.selectedWorkspace()?.[$workspace.status] === workspaceStatus.active,
  );

  protected readonly $tenant: typeof $tenant = $tenant;
  protected readonly Icons: typeof Icons = Icons;
  protected readonly DsButtonVariants: typeof DsButtonVariants = DsButtonVariants;
  protected readonly DsChipColorVariant: typeof DsChipColorVariant = DsChipColorVariant;
  protected readonly DsChipSize: typeof DsChipSize = DsChipSize;
  protected readonly FeatureFlagCollection: typeof FeatureFlagCollection = FeatureFlagCollection;
  protected readonly cardHeadingLevel: HeadingLevelType = 'h2';
  protected readonly RequiredSecurities: typeof RequiredSecurities = RequiredSecurities;
  protected readonly DsBannerState: typeof DsBannerState = DsBannerState;

  constructor(
    private readonly appConfigService: AppConfigService,
    private readonly routingService: RoutingService,
    private readonly userService: UserService,
    private readonly tenantService: TenantService,
    private readonly externalLinkService: ExternalLinkService,
    private readonly aiService: AiService,
    private readonly featureFlagService: FeatureFlagService,
    private readonly loadErrorHandler: LoadErrorHandler,
    private readonly errorMessageService: ErrorMessageService,
    private readonly modalService: DsModalService,
    private readonly navService: NavService,
    private readonly userPropertyService: UserPropertyService,
    private readonly digitalPropertyService: DigitalPropertyService,
    private readonly workspaceService: WorkspaceService,
    private readonly busMessageService: BusMessageService,
    private readonly translateService: TranslateService,
    private readonly notificationService: NotificationService,
    private readonly userAclService: UserAclService,
    private readonly userWayNavigationService: UserWayNavigationService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly packageService: PackageService,
  ) {
    super();
    this.subscriptions = new Subscription();
  }

  setShowArchivedItems(value: boolean): void {
    this.showArchivedItems$.next(value);
    this.refreshOrganizationTree();
  }

  public setSelectedOrganization(focusDescriptionCard: boolean = true): void {
    this.selectedItemType.set(SelectedTreeItemType.organization);
    this.selectedWorkspace.set(null);
    this.selectedDigitalProperty.set(null);
    this.descriptionCardTitle.set(this.currentTenant()?.name ?? '');
    if (focusDescriptionCard) {
      this.focusDescriptionCard();
    }
  }

  public setSelectedWorkspace(workspace: IWorkspace): void {
    this.selectedItemType.set(SelectedTreeItemType.workspace);
    this.selectedDigitalProperty.set(null);
    this.selectedWorkspace.set(workspace);
    this.descriptionCardTitle.set(workspace[$workspace.name]);
    this.focusDescriptionCard();
  }

  public setSelectedDigitalProperty(dp: IDigitalPropertyListItem, types: IDigitalPropertyType[]): void {
    const type: IDigitalPropertyType = types.find(
      (t: IDigitalPropertyType) => t[$digitalPropertyType._id] === dp[$digitalProperty.typeId],
    )!;
    this.selectedItemType.set(
      type[$digitalPropertyType.name] === DigitalPropertyTypeNames.website
        ? SelectedTreeItemType.website
        : SelectedTreeItemType.anotherAsset,
    );
    this.selectedWorkspace.set(null);
    this.selectedDigitalProperty.set(dp);
    this.descriptionCardTitle.set(dp[$digitalProperty.name]);
    this.focusDescriptionCard();
  }

  private findItemInTreeById(tree: DsTreeMenuItem[], id: string): DsTreeMenuItem | null {
    for (const item of tree) {
      if (item.id === id) {
        return item;
      }
      if (item.children) {
        const found = this.findItemInTreeById(item.children, id);
        if (found) {
          return found;
        }
      }
    }
    return null;
  }

  private clearAllActiveStates(tree: DsTreeMenuItem[]): void {
    for (const item of tree) {
      item.active = false;
      if (item.children) {
        this.clearAllActiveStates(item.children);
      }
    }
  }

  private selectOrganizationTreeItem(newSelectedItemId: string): void {
    const currentSelectedId: string | null = this.selectedTreeItemId();
    if (currentSelectedId === newSelectedItemId) {
      return;
    }

    const tree = this.organizationTree();
    this.clearAllActiveStates(tree);

    const item: DsTreeMenuItem | null = this.findItemInTreeById(tree, newSelectedItemId);
    if (!item) {
      return;
    }

    item.active = true;
    this.selectedTreeItemId.set(newSelectedItemId);
    this.organizationTree.set(tree);
  }

  private focusDescriptionCard(): void {
    const el: HTMLElement = this.descriptionCardRef?.nativeElement;
    if (!el) {
      return;
    }
    el.focus({ preventScroll: true });
    const rect: DOMRect = el.getBoundingClientRect();
    if (rect.top < 0 || rect.bottom > window.innerHeight) {
      el.scrollIntoView({ block: 'start', inline: 'nearest', behavior: 'smooth' });
    }
  }

  private getAdminOrganizationData(): Observable<[IDigitalPropertiesListResponse, IWorkspace[], IDigitalPropertyType[]]> {
    const tenantId: string = this.tenantId();
    const digitalPropertiesAdminRequest: Observable<IDigitalPropertiesListResponse> =
      this.tenantService.getAllDigitalAssets(tenantId);
    const workspacesAdminRequest: Observable<IWorkspace[]> = this.tenantService.getWorkspaces(tenantId);
    const digitalPropertyTypesRequest: Observable<IDigitalPropertyType[]> = this.digitalPropertyService.getDigitalPropertyTypes();
    return combineLatest([digitalPropertiesAdminRequest, workspacesAdminRequest, digitalPropertyTypesRequest]);
  }

  private getUserOrganizationData(): Observable<[IDigitalPropertiesListResponse, IWorkspace[], IDigitalPropertyType[]]> {
    return this.userService.userDataChanged$.pipe(
      switchMap((user: IUserServerResponse) =>
        combineLatest([
          this.userPropertyService.getAvailableDigitalProperties(undefined, true),
          this.workspaceService
            .getAllUserWorkspaces({
              [ApiQueryOption.filterByUserId]: user?.[$user._id],
              [ApiQueryOption.limit]: 0,
              [ApiQueryOption.page]: 0,
              [ApiQueryOption.status]: $sortingOrder.all,
            })
            .pipe(map((workspacesResponse: IGetWorkspacesResponse) => workspacesResponse.workspaces)),
          this.digitalPropertyService.getDigitalPropertyTypes(),
        ]),
      ),
      shareReplay(1),
    );
  }

  private getOrganizationTree(): Observable<DsTreeMenuItem[]> {
    const cachedData$ = this.isAdminView ? this.getAdminOrganizationData() : this.getUserOrganizationData();

    return combineLatest([cachedData$, this.showArchivedItems$]).pipe(
      map(
        ([[digitalProperties, workspaces, types]]: [
          [IDigitalPropertiesListResponse, IWorkspace[], IDigitalPropertyType[]],
          boolean,
        ]) => {
          this.allWorkspaces.set(workspaces);
          this.allDigitalProperties.set(digitalProperties?.items);

          const organizationNode: DsTreeMenuItem = {
            id: this.currentTenant()?.[$tenant._id],
            label: this.currentTenant()?.[$tenant.name],
            icon: Icons.Building,
            active: true,
            expanded: true,
            children: this.mapOrganizationTreeItems(types),
          };

          organizationNode.onClick = (): void => {
            this.selectOrganizationTreeItem(organizationNode.id);
            this.setSelectedOrganization();
          };

          this.selectOrganizationTreeItem(organizationNode.id);
          this.setSelectedOrganization(false);

          return [organizationNode];
        },
      ),
    );
  }

  private mapOrganizationTreeItems(types: IDigitalPropertyType[]): DsTreeMenuItem[] {
    const workspacesMap: Record<string, DsTreeMenuItem> = {};

    const workspaceLookup = new Map(
      this.allWorkspaces()
        .filter((w: IWorkspace) => this.showArchivedItems() || w[$workspace.status] === workspaceStatus.active)
        .map((w: IWorkspace) => [w[$workspace._id], w]),
    );
    const filteredDigitalProperties = this.allDigitalProperties().filter(
      (property: IDigitalPropertyListItem) =>
        this.showArchivedItems() || property[$digitalProperty.status] === DigitalPropertyStatus.active,
    );

    const createWorkspaceItem = (
      workspaceId: string,
      workspaceName: string,
      workspaceStatusValue: workspaceStatus,
    ): DsTreeMenuItem => {
      let label = workspaceName;
      if (workspaceStatusValue === workspaceStatus.archived) {
        label = `${workspaceName} (${this.translateService.instant('archived')})`;
      }

      if (workspaceStatusValue === workspaceStatus.closed) {
        label = `${workspaceName} (${this.translateService.instant('closed')})`;
      }

      const item: DsTreeMenuItem = {
        label,
        id: workspaceId,
        icon: Icons.Briefcase,
        children: [],
        expanded: false,
        active: false,
        onClick: () => {
          this.selectOrganizationTreeItem(workspaceId);
          const entity = workspaceLookup.get(workspaceId);
          if (entity) {
            this.setSelectedWorkspace(entity);
          }
        },
      };
      return item;
    };

    const createDigitalPropertyItem = (property: IDigitalPropertyListItem): DsTreeMenuItem => {
      const label =
        property[$digitalProperty.status] === DigitalPropertyStatus.archived
          ? `${property[$digitalProperty.name]} (${this.translateService.instant('archived')})`
          : property[$digitalProperty.name];

      const item: DsTreeMenuItem = {
        id: property[$digitalProperty._id],
        label,
        icon: this.navService.getIconForDigitalPropertyTypeId(property[$digitalProperty.typeId], types),
        expanded: false,
        onClick: () => {
          this.selectOrganizationTreeItem(item.id);
          this.setSelectedDigitalProperty(property, types);
        },
      };

      return item;
    };

    const sortTreeMenuItems = (a: DsTreeMenuItem, b: DsTreeMenuItem): number => {
      const closedLabel = `(${this.translateService.instant('closed')})`;
      const archivedLabel = `(${this.translateService.instant('archived')})`;
      const aIsArchived = [closedLabel, archivedLabel].some((label: string) => a.label.includes(label));
      const bIsArchived = [closedLabel, archivedLabel].some((label: string) => b.label.includes(label));

      if (aIsArchived !== bIsArchived) {
        return aIsArchived ? 1 : -1;
      }

      return a.label.localeCompare(b.label);
    };

    // Map each digital property into its workspace tree
    filteredDigitalProperties.forEach((property: IDigitalPropertyListItem) => {
      const workspace = property[$digitalProperty.workspace];
      const workspaceId = workspace[$workspace._id];
      const workspaceName = workspace[$workspace.name];
      const workspaceStatusValue = workspace[$workspace.status];

      // Ensure workspace item exists
      let workspaceItem = workspacesMap[workspaceId];
      if (!workspaceItem) {
        workspaceItem = workspacesMap[workspaceId] = createWorkspaceItem(
          workspaceId,
          workspaceName,
          workspaceStatusValue as workspaceStatus,
        );
      }

      // Add DP as child
      workspaceItem.children.push(createDigitalPropertyItem(property));
    });

    // Sort workspaces and their digital properties: active first, archived last (both alphabetically)
    return Object.values(workspacesMap)
      .map((workspaceItem: DsTreeMenuItem) => ({
        ...workspaceItem,
        children: (workspaceItem.children ?? []).sort(sortTreeMenuItems),
      }))
      .sort(sortTreeMenuItems);
  }

  private refreshOrganizationTree(): void {
    if (this.organizationTreeSubscription) {
      this.subscriptions.remove(this.organizationTreeSubscription);
      this.organizationTreeSubscription.unsubscribe();
      this.organizationTreeSubscription = null;
    }

    const currentTree = this.organizationTree();

    const workspacesIdsToExpand: Set<string> = !currentTree?.[0]?.children
      ? new Set()
      : new Set(currentTree[0].children.filter((item: DsTreeMenuItem) => item.expanded).map((item: DsTreeMenuItem) => item.id));

    this.organizationTreeSubscription = this.getOrganizationTree()
      .pipe(
        first(),
        map((tree: DsTreeMenuItem[]) => {
          tree[0].children.forEach((item: DsTreeMenuItem) => {
            if (workspacesIdsToExpand.has(item.id)) {
              item.expanded = true;
            }
          });

          this.organizationTree.set(tree);

          if (this.pendingSelectedItemId && tree.length > 0) {
            this.selectTreeItem(tree[0], this.pendingSelectedItemId);
          }
        }),
      )
      .subscribe({
        error: (error: HttpErrorResponse) => {
          this.loadErrorHandler.handleError('get_tenant_error', error, OrganizationDetailsComponent.name);
        },
      });

    this.subscriptions.add(this.organizationTreeSubscription);
  }

  ngOnInit(): void {
    this.pageTitle = this.routingService.getPageTitle();
    this.refreshOrganizationTree();

    this.subscriptions.add(
      this.busMessageService.from(BusMessageChannels.digitalPropertyChange, BusMessageChannels.workspaceCreated).subscribe(() => {
        this.refreshOrganizationTree();
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  onContactSupportClick(e: MouseEvent): void {
    e.preventDefault();

    CommonUtility.navigateToExternalUrl(this.externalLinkService.getProductSupportFormUrl(), true);
  }

  onTabChange(tabId: number): void {
    this.activeTabId = tabId;
  }

  private selectTreeItem(currentItem: DsTreeMenuItem, itemToSelectId: string): boolean {
    if (currentItem.id === itemToSelectId) {
      currentItem.onClick();
      return true;
    }

    if (!currentItem.children) {
      return false;
    }

    for (const child of currentItem.children) {
      if (this.selectTreeItem(child, itemToSelectId)) {
        currentItem.expanded = true;
        return true;
      }
    }
    return false;
  }

  public onCreateWorkspace(): void {
    const modalRef: DsModalRef = this.modalService.open(AddWorkspaceModalComponent, DsModalVariant.center);
    modalRef.componentInstance.tenantId = this.currentTenant()?.[$tenant._id];
    modalRef.result.then((newWorkspace?: IWorkspace) => {
      if (SharedCommonUtility.notNullish(newWorkspace)) {
        this.pendingSelectedItemId = newWorkspace[$workspace._id];
      }
      this.refreshOrganizationTree();
    });
  }

  public onEditWorkspace(): void {
    const selectedWorkspace: IWorkspace = this.selectedWorkspace();
    const modalRef: DsModalRef = this.modalService.open(EditWorkspaceModalComponent, DsModalVariant.center);
    modalRef.componentInstance.workspaceId = selectedWorkspace[$workspace._id];
    modalRef.componentInstance.workspaceName = selectedWorkspace[$workspace.name];
    modalRef.componentInstance.workspaceDescription = selectedWorkspace[$workspace.description];
    modalRef.result.then(() => {
      // Selects edited workspace in tree and shows on right card
      this.pendingSelectedItemId = selectedWorkspace[$workspace._id];
      this.refreshOrganizationTree();
    });
  }

  public onEditDigitalProperty(): void {
    const selectedDigitalPropertyId: string = this.selectedDigitalProperty()[$digitalProperty._id];
    const modalRef: DsModalRef = this.modalService.open(EditDigitalPropertyModalComponent, DsModalVariant.center);
    modalRef.componentInstance.workspaceId = this.selectedDigitalProperty()[$digitalProperty.workspaceId];
    modalRef.componentInstance.digitalPropertyId = selectedDigitalPropertyId;
    modalRef.componentInstance.isWebsite = this.selectedItemType() === SelectedTreeItemType.website;
    modalRef.result.then(() => {
      // Selects edited digital asset in tree and shows on right card
      this.pendingSelectedItemId = selectedDigitalPropertyId;
      this.refreshOrganizationTree();
    });
  }

  public onCreateDigitalProperty(): void {
    const modalRef: DsModalRef = this.modalService.open(AddDigitalPropertiesModalComponent, DsModalVariant.center);
    const currentSelectedItemId: string = this.selectedTreeItemId();
    modalRef.componentInstance.tenantId = this.tenantId();
    modalRef.result.then((addedDigitalProperty?: IDigitalProperty) => {
      // If user uses bulk digital asset creation we do not change selected tree item,
      // otherwise newly created single digital asset will be selected and shown
      this.pendingSelectedItemId = addedDigitalProperty ? addedDigitalProperty[$digitalProperty._id] : currentSelectedItemId;
      this.refreshOrganizationTree();
    });
  }

  public async onMoveDigitalProperty(): Promise<void> {
    const dp: IDigitalPropertyListItem = this.selectedDigitalProperty();
    if (!dp) {
      return;
    }

    const workspaceId: string = dp[$digitalProperty.workspace][$workspace._id].toString();
    const digitalPropertyId: string = dp[$digitalProperty._id].toString();

    this.isMoveValidationInProgress.set(true);

    const subscription: Subscription = this.digitalPropertyService
      .validateMove(workspaceId, digitalPropertyId)
      .pipe(finalize(() => this.isMoveValidationInProgress.set(false)))
      .subscribe({
        next: () => {
          const modalRef: DsModalRef = this.modalService.open(MoveDigitalPropertyModalComponent, DsModalVariant.center);
          modalRef.componentInstance.workspaceId = workspaceId;
          modalRef.componentInstance.availableWorkspaces = this.availableDestinationWorkspaces();
          modalRef.componentInstance.digitalProperty = dp;
          modalRef.componentInstance.allDigitalProperties = this.allDigitalProperties();

          modalRef.result.then(() => {
            const message: string = this.translateService.instant('digital_asset_moved');
            this.notificationService.success(message, NotificationPosition.Toast, true);

            this.pendingSelectedItemId = digitalPropertyId;
            this.refreshOrganizationTree();
          });
        },
        error: (error: HttpErrorResponse) => {
          let errorMessage: string;
          const errorCode: number = error?.error?.app?.code;
          if (errorCode === 1240) {
            errorMessage = this.translateService.instant('cannot_move_digital_property_last_active_in_workspace');
          } else if (errorCode === 1241) {
            errorMessage = this.translateService.instant('cannot_move_digital_asset_mature');
          } else {
            errorMessage = this.errorMessageService.getGlobalErrorResponse(error);
          }

          const modalRef: DsModalRef = this.modalService.open(CannotMoveDigitalPropertyModalComponent, DsModalVariant.center);
          modalRef.componentInstance.reason = errorMessage;
        },
      });

    this.subscriptions.add(subscription);
  }

  public async onChangeDigitalPropertyState(action: DigitalPropertyStateAction): Promise<void> {
    const selectedProperty = this.selectedDigitalProperty();
    if (!selectedProperty) {
      return;
    }

    const selectedPropertyId: string = selectedProperty[$digitalProperty._id];
    const modalRef: DsModalRef = this.modalService.open(
      SelfmanageChangeDigitalPropertyStateModalComponent,
      DsModalVariant.center,
    );
    const dp: IDigitalPropertyListItem = this.selectedDigitalProperty();
    modalRef.componentInstance.workspaceId = dp[$digitalProperty.workspace][$workspace._id];
    modalRef.componentInstance.digitalPropertyId = dp[$digitalProperty._id];
    modalRef.componentInstance.changeTo = action;

    modalRef.result.then((actionType: DigitalPropertyStateAction) => {
      const translationKey: string =
        actionType === DigitalPropertyStateAction.archive ? 'digital_asset_archived' : 'digital_asset_restored';
      const message: string = this.translateService.instant(translationKey);
      this.notificationService.success(message, NotificationPosition.Toast, true);

      this.pendingSelectedItemId = selectedPropertyId;
      this.refreshOrganizationTree();
    });
  }

  public async onChangeWorkspaceState(action: WorkspaceStateAction): Promise<void> {
    const selectedWorkspace = this.selectedWorkspace();
    if (!selectedWorkspace) {
      return;
    }

    const selectedWorkspaceId: string = selectedWorkspace[$workspace._id];
    const busMessageChannel: BusMessageChannels = BusMessageChannels.workspaceUpdated;

    const modalRef: DsModalRef = this.modalService.open(SelfmanageChangeWorkspaceStateModalComponent, DsModalVariant.center);
    modalRef.componentInstance.workspaceId = selectedWorkspaceId;
    modalRef.componentInstance.changeTo = action;

    if (action === WorkspaceStateAction.restore) {
      const digitalAssetsUsage = this.digitalAssetsUsage();

      if (digitalAssetsUsage.count >= digitalAssetsUsage.limit) {
        modalRef.componentInstance.restoreError = 'error_workspace_restore_no_space_for_assets';
      }
    }

    modalRef.result.then((result: { actionType: WorkspaceStateAction; restoreResponse?: IRestoreWorkspaceResponse }) => {
      const translationKey: string =
        result.actionType === WorkspaceStateAction.archive ? 'archive_workspace_success' : 'restore_workspace_success';
      const message: string = this.translateService.instant(translationKey);
      this.notificationService.success(message, NotificationPosition.Toast, true);

      if (result.actionType === WorkspaceStateAction.restore && result.restoreResponse?.partiallyRestored) {
        const warningMessage: string = this.translateService.instant('restore_workspace_partial_warning');
        this.notificationService.warn(warningMessage, NotificationPosition.Toast, true);
      }

      this.busMessageService.to(busMessageChannel).next();
      this.pendingSelectedItemId = selectedWorkspaceId;
      this.refreshOrganizationTree();
    });
  }

  public onConfigureWidgetClicked(): void {
    this.userWayNavigationService.navigateToUserWayDashboard(this.onConfigureWidgetError.bind(this));
  }

  private onConfigureWidgetError(): void {
    this.showUserWaySsoErrorBanner = true;
  }

  public onUserWaySsoErrorBannerClosed(): void {
    this.showUserWaySsoErrorBanner = false;
  }

  public onOpenStatementClicked(): void {
    const dp: IDigitalPropertyListItem = this.selectedDigitalProperty();
    const normalizedUrl: URL = SharedCommonUtility.getNormalizedUrl(dp[$digitalProperty.accessibilityStatement]);
    CommonUtility.navigateToExternalUrl(normalizedUrl.href, true);
  }

  protected readonly SelectedTreeItemType: typeof SelectedTreeItemType = SelectedTreeItemType;
  protected readonly DigitalPropertyStatus: typeof DigitalPropertyStatus = DigitalPropertyStatus;
  protected readonly DigitalPropertyStateAction: typeof DigitalPropertyStateAction = DigitalPropertyStateAction;
  protected readonly WorkspaceStateAction: typeof WorkspaceStateAction = WorkspaceStateAction;
  protected readonly workspaceStatus: typeof workspaceStatus = workspaceStatus;
  protected readonly DsToggleLabelPosition: typeof DsToggleLabelPosition = DsToggleLabelPosition;
  public readonly descriptionFieldConfig: Signal<IDisplayFieldConfig | null> = computed(() => {
    const showInternalNotes: boolean = this.showInternalNotesHideDescription();
    const tenant = this.currentTenant();

    if (showInternalNotes && !this.isAdminView) {
      return null;
    }

    const fieldValue: string | undefined = showInternalNotes ? tenant?.[$tenant.internalNotes] : tenant?.[$tenant.description];

    return {
      label: showInternalNotes ? 'tenant_internal_notes' : 'tenant_description',
      field: fieldValue ?? '',
    };
  });
}
